import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Scale, ArrowRight, ArrowLeft, FileText, Building2, Heart, Users, Briefcase,
  ShieldCheck, TrendingUp, Clock, Globe, Send, Loader2, Phone, Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import AvocatEnOrLogo from "../components/AvocatEnOrLogo";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { isIOS } from "../lib/platform";

const SEGMENTS = [
  { icon: ShieldCheck, label: "Assurances Protection Juridique", desc: "DAS, Arag, Euromex, Axa…" },
  { icon: Heart, label: "Mutuelles", desc: "Solidaris, Partenamut, MC, Helan" },
  { icon: Users, label: "Syndicats", desc: "CSC, FGTB, CGSLB" },
  { icon: Building2, label: "CPAS & ASBL", desc: "Médiation, accompagnement social" },
  { icon: Briefcase, label: "Cabinets juridiques", desc: "Avocats, notaires, médiateurs" },
];

const BENEFITS = [
  { icon: TrendingUp, title: "Réduisez vos appels de niveau 1 de 40%", body: "Vos assurés trouvent eux-mêmes la réponse pour les courriers, contrôles et arnaques courantes. Vos avocats se concentrent sur les cas complexes." },
  { icon: Clock, title: "Réponse en 5 secondes au lieu de 5 jours", body: "L'IA travaille 24h/24, 365 jours/an. Plus de file d'attente, plus de boîte vocale, plus de stress." },
  { icon: ShieldCheck, title: "Plus de 200 arnaques détectées en moyenne / 1000 utilisateurs", body: "Vos clients seniors sont protégés contre les faux Bpost, faux huissiers, faux fils en détresse, deepfakes vocaux." },
  { icon: Globe, title: "White-label complet", body: "Votre logo, vos couleurs, votre nom de domaine, votre marque. L'app devient un service premium de votre offre." },
];

export default function Pro() {
  const [form, setForm] = useState({ org: "", contact: "", email: "", phone: "", segment: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const hidePricing = isIOS(); // Apple Guidelines 3.1.1 : pas de paiement externe visible dans l'app iOS

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org.trim() || !form.contact.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Merci de remplir les champs obligatoires");
      return;
    }
    setSending(true);
    try {
      const url = process.env.REACT_APP_BACKEND_URL + "/api/public/pro-contact";
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Erreur d'envoi");
      setSent(true);
      toast.success("Merci ! Nous revenons vers vous sous 48 h.");
    } catch {
      toast.error("Erreur — réessayez ou écrivez directement à pro@unavocatenor.com");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="pro-page">
      {/* NAV */}
      <nav className="border-b border-slate-200 bg-white/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pro-nav-logo">
            <AvocatEnOrLogo size={36} variant="full" background="black" className="rounded-sm" />
            <span className="font-serif-display text-xl font-semibold tracking-tight">Un Avocat en Or</span>
            <span className="ml-2 text-[10px] uppercase tracking-[0.2em] bg-[#B89C65] text-white px-2 py-0.5 rounded-sm">Pro</span>
          </Link>
          <Link to="/" data-testid="pro-back-home">
            <Button variant="ghost" className="rounded-sm text-sm h-9">
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Particuliers
            </Button>
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-4">Pour les professionnels</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-tight mb-5">
            Offrez à vos assurés Un Avocat en Or, en marque blanche.
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Une application juridique d'urgence intégrée à votre offre.
            Vos clients sont autonomes sur 80% des cas courants. Vos juristes gardent les 20% qui comptent.
          </p>
          <a href="#contact" data-testid="pro-hero-cta">
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 h-12 px-8 text-base">
              Demander une démo <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="py-16 sm:py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3 text-center">Pour qui</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight text-center mb-12">
            Conçu pour 5 types d'organisations
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {SEGMENTS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-sm p-5 hover:border-slate-900 hover:shadow-md transition-all" data-testid={`pro-segment-${i}`}>
                  <div className="h-11 w-11 rounded-sm bg-slate-900 text-[#B89C65] flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-serif-display text-base font-medium leading-tight mb-1">{s.label}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 sm:py-20 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3 text-center">Pourquoi nous</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight text-center mb-12">
            4 raisons d'intégrer Un Avocat en Or
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="flex gap-4" data-testid={`pro-benefit-${i}`}>
                  <div className="h-12 w-12 rounded-sm bg-[#B89C65]/10 text-[#B89C65] flex items-center justify-center flex-shrink-0">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-serif-display text-xl font-medium leading-tight mb-2">{b.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{b.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING / MODEL — masqué sur iOS (Apple Guidelines 3.1.1) */}
      {!hidePricing && (
      <section className="py-16 sm:py-20 bg-slate-900 text-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3">Grille tarifaire — lancement 2026</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight mb-3">
            Particuliers, familles, entreprises
          </h2>
          <p className="text-sm text-white/70 max-w-2xl mx-auto mb-10">
            Tarifs fixes, en clair, sans piège. La formule Famille est unique : peu importe le nombre d'enfants (3, 10, 19 ou plus), le prix ne bouge pas.
          </p>

          {/* B2C */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mb-10 max-w-3xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-sm p-6" data-testid="plan-individual">
              <p className="text-xs uppercase tracking-widest text-[#B89C65] mb-2">Individuel</p>
              <p className="font-serif-display text-4xl font-medium mb-1">9,50 € <span className="text-sm font-normal text-white/60">TTC</span></p>
              <p className="text-xs text-white/60 mb-5">/ mois · une personne · TVA 21% incluse</p>
              <ul className="space-y-2 text-sm text-white/80">
                <li>→ 13 touches SOS illimitées</li>
                <li>→ Consultation sécurisée & QR médical</li>
                <li>→ Export PDF & historique complet</li>
                <li>→ 12 langues + RTL</li>
              </ul>
            </div>
            <div className="bg-[#B89C65]/10 border-2 border-[#B89C65] rounded-sm p-6 relative" data-testid="plan-family">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#B89C65] text-slate-900 text-[10px] uppercase tracking-widest px-3 py-1 rounded-sm font-bold">Solidaire</span>
              <p className="text-xs uppercase tracking-widest text-[#B89C65] mb-2">Famille</p>
              <p className="font-serif-display text-4xl font-medium mb-1">14,50 € <span className="text-sm font-normal text-white/60">TTC</span></p>
              <p className="text-xs text-white/60 mb-5">/ mois · tarif unique quel que soit le nb d'enfants · TVA 21% incluse</p>
              <ul className="space-y-2 text-sm text-white/80">
                <li>→ Parents + <strong>3, 10, 19 enfants ou plus</strong> — même prix</li>
                <li>→ QR médical familial partageable</li>
                <li>→ Refuge solidaire prioritaire</li>
                <li>→ Toutes les fonctions Individuel</li>
              </ul>
            </div>
          </div>

          {/* B2B */}
          <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3 mt-14">Entreprises · Assurances · Mutuelles · Syndicats</p>
          <h3 className="font-serif-display text-2xl sm:text-3xl font-medium tracking-tight mb-8">Quatre paliers, tarifs fermes</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
            <div className="bg-white/5 border border-white/10 rounded-sm p-6" data-testid="plan-b2b-tpe">
              <p className="text-xs uppercase tracking-widest text-[#B89C65] mb-2">Pack TPE</p>
              <p className="font-serif-display text-3xl font-medium mb-1">249 € <span className="text-xs font-normal text-white/60">HT</span></p>
              <p className="text-xs text-white/60 mb-5">/ mois · &lt; 50 personnes</p>
              <ul className="space-y-1.5 text-xs text-white/80">
                <li>→ Forfait global</li>
                <li>→ White-label basique</li>
                <li>→ Support email prioritaire</li>
              </ul>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-sm p-6" data-testid="plan-b2b-pme">
              <p className="text-xs uppercase tracking-widest text-[#B89C65] mb-2">Pack PME</p>
              <p className="font-serif-display text-3xl font-medium mb-1">399 € <span className="text-xs font-normal text-white/60">HT</span></p>
              <p className="text-xs text-white/60 mb-5">/ mois · 50 à 99 personnes</p>
              <ul className="space-y-1.5 text-xs text-white/80">
                <li>→ Forfait global</li>
                <li>→ White-label complet</li>
                <li>→ Tableau de bord usage</li>
              </ul>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-sm p-6" data-testid="plan-b2b-corporate">
              <p className="text-xs uppercase tracking-widest text-[#B89C65] mb-2">Pack Corporate</p>
              <p className="font-serif-display text-3xl font-medium mb-1">449 € <span className="text-xs font-normal text-white/60">HT</span></p>
              <p className="text-xs text-white/60 mb-5">/ mois · 100 à 149 personnes</p>
              <ul className="space-y-1.5 text-xs text-white/80">
                <li>→ Forfait global</li>
                <li>→ Domaine personnalisé</li>
                <li>→ Hot-line + account manager</li>
              </ul>
            </div>
            <div className="bg-[#B89C65]/10 border-2 border-[#B89C65] rounded-sm p-6" data-testid="plan-b2b-enterprise">
              <p className="text-xs uppercase tracking-widest text-[#B89C65] mb-2">Grand Compte</p>
              <p className="font-serif-display text-3xl font-medium mb-1">2,49 € <span className="text-xs font-normal text-white/60">HT</span></p>
              <p className="text-xs text-white/60 mb-5">/ mois · par employé — 150+</p>
              <ul className="space-y-1.5 text-xs text-white/80">
                <li>→ Tarif variable par siège</li>
                <li>→ Intégration API + SLA</li>
                <li>→ Hébergement dédié EU</li>
                <li>→ Personnalisations sur mesure</li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-white/50 mt-8">
            <strong>B2C</strong> : prix TTC, TVA 21% incluse (loi belge sur l'affichage consommateur). <strong>B2B</strong> : prix HT, TVA en sus selon régime (reverse charge intracommunautaire disponible pour les clients avec n° TVA valide). Facturation mensuelle · résiliable à tout moment · devis officiel envoyé après contact.
          </p>
        </div>
      </section>
      )}

      {/* iOS : bloc de substitution neutre (aucun prix ni CTA paiement) */}
      {hidePricing && (
        <section className="py-16 sm:py-20 bg-slate-900 text-white border-b border-slate-200" data-testid="ios-pricing-fallback">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3">Version iPhone</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight mb-4">
              Utilisez l'application gratuitement
            </h2>
            <p className="text-sm text-white/70 max-w-xl mx-auto mb-6">
              Toutes les fonctions essentielles sont accessibles sans compte payant. Vous êtes une organisation (assurance, mutuelle, syndicat, entreprise) ? Contactez-nous pour un accompagnement dédié.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-[#B89C65] text-slate-900 hover:bg-[#a4864e] px-6 py-3 rounded-sm text-sm font-medium transition"
              data-testid="ios-contact-cta"
            >
              Nous contacter
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3 text-center">Parlons-en</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight text-center mb-3">
            Demander une démo
          </h2>
          <p className="text-sm text-slate-600 text-center mb-10">
            Réponse personnalisée sous 48 h. Démo en direct possible (visio ou présentiel Bruxelles).
          </p>

          {sent ? (
            <div className="bg-white border-2 border-emerald-500 rounded-sm p-8 text-center" data-testid="pro-contact-sent">
              <ShieldCheck className="h-12 w-12 mx-auto text-emerald-600 mb-3" />
              <h3 className="font-serif-display text-2xl font-medium mb-2">Merci !</h3>
              <p className="text-sm text-slate-600 mb-5">
                Votre demande est bien reçue. Nous revenons vers vous sous 48 h ouvrées.
              </p>
              <Button onClick={() => { setSent(false); setForm({ org: "", contact: "", email: "", phone: "", segment: "", message: "" }); }} variant="outline" className="rounded-sm">
                Nouvelle demande
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 space-y-4" data-testid="pro-contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest mb-1.5 block">Organisation *</Label>
                  <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="Mutuelle X / Assurance Y" className="rounded-sm" required data-testid="pro-form-org" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest mb-1.5 block">Votre nom *</Label>
                  <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Marie Dupont" className="rounded-sm" required data-testid="pro-form-contact" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest mb-1.5 block">Email pro *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="marie@mutuelle.be" className="rounded-sm" required data-testid="pro-form-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest mb-1.5 block">Téléphone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+32 470 …" className="rounded-sm" data-testid="pro-form-phone" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1.5 block">Secteur</Label>
                <select
                  value={form.segment}
                  onChange={(e) => setForm({ ...form, segment: e.target.value })}
                  className="w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                  data-testid="pro-form-segment"
                >
                  <option value="">— Choisir —</option>
                  <option value="assurance">Assurance Protection Juridique</option>
                  <option value="mutuelle">Mutuelle</option>
                  <option value="syndicat">Syndicat</option>
                  <option value="cpas">CPAS / Service social</option>
                  <option value="cabinet">Cabinet juridique</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1.5 block">Votre besoin *</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  placeholder="Ex: Nous avons 50 000 affiliés, nous cherchons une solution pour réduire les appels juridiques de premier niveau..."
                  className="rounded-sm"
                  required
                  data-testid="pro-form-message"
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 w-full"
                data-testid="pro-form-submit"
              >
                {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi…</> : <><Send className="h-4 w-4 mr-2" />Envoyer la demande</>}
              </Button>

              <div className="border-t border-slate-200 pt-4 mt-2">
                <p className="text-xs text-slate-500 text-center mb-2">Ou contactez-nous directement :</p>
                <div className="flex flex-wrap justify-center gap-4 text-sm">
                  <a href="mailto:pro@unavocatenor.com" className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900">
                    <Mail className="h-3.5 w-3.5" /> pro@unavocatenor.com
                  </a>
                </div>
              </div>

              <a
                href={(process.env.REACT_APP_BACKEND_URL || "") + "/api/public/dossier-cpas"}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-xs text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline mt-3"
                data-testid="pro-download-dossier"
              >
                📄 Télécharger le dossier projet (PDF, 1 page)
              </a>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p className="text-white/60">© 2026 Un Avocat en Or</p>
          <div className="flex gap-4 text-white/60">
            <Link to="/" className="hover:text-white">Particuliers</Link>
            <a href="mailto:pro@unavocatenor.com" className="hover:text-white">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
